import { gql } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import { Button } from '@apollo/orbit';
import React from 'react';
import { useLocation } from 'react-router';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { ConditionalLink } from 'src/components/conditionalLink/ConditionalLink';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import {
  getLinkToWorkflow,
  getLinkToWorkflowFragment,
} from '../../helpers/getLinkToWorkflow';

import {
  ChecksTableWorkflowCell,
  ChecksTableWorkflowCellWorkflowFragment,
} from './ChecksTableWorkflowCell';
import {
  CheckWorkflowStatusBadge,
  CheckWorkflowStatusBadgeCheckWorkflowFragment,
} from './CheckWorkflowStatusBadge';
import {
  DownstreamWorkflowStatusCounts,
  DownstreamWorkflowStatusCountsFragment,
} from './DownstreamWorkflowStatusCounts';

interface Props {
  workflow: GraphQLTypes.CheckWorkflowItemFragment;
  isDrawerOpen: boolean;
  onToggleDrawer: (value: string) => void;
}

export const CheckWorkflowItemFragment = gql`
  fragment CheckWorkflowItemFragment on CheckWorkflow {
    id
    gitContext {
      branch
      commit
      committer
      commitUrl
    }
    triggeredBy {
      id
      ... on User {
        fullName
        avatarUrl
      }
    }
    subgraphNames
    status
    baseVariant {
      id
      name
    }
    createdAt
    startedAt
    completedAt
    isSandboxCheck
    introspectionEndpoint
    downstreamTask {
      id
      ...DownstreamWorkflowStatusCountsFragment
    }
    ...getLinkToWorkflowFragment
    ...CheckWorkflowStatusBadgeCheckWorkflowFragment
    ...ChecksTableWorkflowCellWorkflowFragment
  }

  ${DownstreamWorkflowStatusCountsFragment}
  ${getLinkToWorkflowFragment}
  ${CheckWorkflowStatusBadgeCheckWorkflowFragment}
  ${ChecksTableWorkflowCellWorkflowFragment}
`;

export const CheckWorkflowItem = ({
  workflow,
  isDrawerOpen,
  onToggleDrawer,
}: Props) => {
  const location = useLocation();

  const internalGraphLinking = useInternalGraphLinking();

  return (
    <div className="my-2">
      <div className="flex items-center">
        <CheckWorkflowStatusBadge workflow={workflow} className="mr-2" />
        <div className="truncate text-base font-semibold text-link">
          {/* variant name */}
          {workflow.baseVariant?.name ? (
            <ConditionalLink
              className="hover:underline"
              to={
                getLinkToWorkflow(workflow, location, internalGraphLinking) ??
                undefined
              }
            >
              {workflow.baseVariant.name}
            </ConditionalLink>
          ) : (
            <span className="italic text-secondary">unavailable</span>
          )}
        </div>
      </div>
      <div className="ml-1 flex items-center pt-2 text-sm text-secondary">
        <ChecksTableWorkflowCell workflow={workflow} />
      </div>
      {workflow.downstreamTask?.results?.length ? (
        <Button
          className="-ml-2 mt-1.5"
          variant="hidden"
          size="sm"
          rightIcon={isDrawerOpen ? <IconChevronUp /> : <IconChevronDown />}
          onClick={() => onToggleDrawer(workflow.id)}
        >
          <DownstreamWorkflowStatusCounts
            downstreamTask={workflow.downstreamTask}
            className="mr-1"
          />
        </Button>
      ) : null}
    </div>
  );
};
